import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrackCard } from '../components/TrackCard';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Music2, DollarSign, ShoppingBag, ArrowLeft, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ArtistDetailPage = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingChat, setOpeningChat] = useState(false);

  const handleMessageArtist = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to message the artist');
      navigate('/login');
      return;
    }
    if (user?.id === artistId) {
      toast.info("That's you — can't message yourself");
      return;
    }
    setOpeningChat(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/conversations`, {
        artist_id: artistId,
      });
      navigate(`/inbox/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open conversation');
    } finally {
      setOpeningChat(false);
    }
  };

  useEffect(() => {
    const fetchArtist = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/artists/${artistId}`);
        setArtist(response.data);
      } catch (error) {
        console.error('Error fetching artist:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtist();
  }, [artistId]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center">
        <p className="text-slate-400">Artist not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <Link
          to="/artists"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Artists
        </Link>

        {/* Artist Header */}
        <div className="flex flex-col md:flex-row gap-8 mb-12">
          {/* Avatar */}
          <div className="w-full md:w-64 shrink-0">
            <div className="aspect-square bg-slate-800 rounded-xl overflow-hidden">
              {artist.avatar_url ? (
                <img
                  src={artist.avatar_url}
                  alt={artist.artist_name || artist.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500/20 to-cyan-500/20">
                  <Music2 className="w-20 h-20 text-emerald-500/50" />
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-2 block">
              Artist
            </span>
            <h1 className="text-4xl font-medium text-white mb-4" data-testid="artist-name">
              {artist.artist_name || artist.name}
            </h1>
            {artist.bio && (
              <p className="text-slate-400 text-lg leading-relaxed mb-6 max-w-2xl">
                {artist.bio}
              </p>
            )}

            {user?.id !== artistId && (
              <Button
                onClick={handleMessageArtist}
                disabled={openingChat}
                className="mb-6 bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold rounded-lg"
                data-testid="artist-message-btn"
              >
                {openingChat ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                Message Artist
              </Button>
            )}

            {/* Stats */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                  <Music2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-white">{artist.tracks?.length || 0}</p>
                  <p className="text-sm text-slate-500">Tracks</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-white">{artist.total_sales || 0}</p>
                  <p className="text-sm text-slate-500">Sales</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-white">
                    {formatCurrency(artist.total_earnings || 0)}
                  </p>
                  <p className="text-sm text-slate-500">Earned</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tracks */}
        <div>
          <h2 className="text-2xl font-medium text-white mb-6">Tracks by {artist.artist_name || artist.name}</h2>
          
          {!artist.tracks || artist.tracks.length === 0 ? (
            <div className="bg-slate-900/50 border border-white/10 p-12 rounded-xl text-center">
              <Music2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No tracks available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="artist-tracks-grid">
              {artist.tracks.map((track) => (
                <TrackCard key={track.id} track={track} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
